import { useEffect, useState } from "react";
import {
  CreditCard,
  Loader2,
  LockKeyhole,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";

export default function PaymentIntegration() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [paymentsFeatureEnabled, setPaymentsFeatureEnabled] =
    useState(false);

  const [availableProviders, setAvailableProviders] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [maskedKey, setMaskedKey] = useState("");

  const [form, setForm] = useState({
    online_payments_enabled: false,
    provider: "",
    mode: "test",
    key_id: "",
    key_secret: "",
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await api.get(
        "/hospital/payment-settings"
      );

      const providers =
        data.available_providers || [];

      setAvailableProviders(providers);

      setPaymentsFeatureEnabled(
        !!data.payments_feature_enabled
      );

      setConfigured(
        !!data.integration?.is_configured
      );

      setMaskedKey(
        data.integration?.key_id_masked || ""
      );

      const selectedProvider =
        data.active_provider ||
        data.integration?.provider ||
        providers[0]?.key ||
        "";

      setForm({
        online_payments_enabled:
          !!data.online_payments_enabled,

        provider: selectedProvider,

        mode:
          data.integration?.mode ||
          "test",

        key_id: "",
        key_secret: "",
      });
    } catch (e) {
      setError(
        apiError(
          e,
          "Failed to load payment settings"
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const update = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const changeProvider = (provider) => {
    setForm((prev) => ({
      ...prev,
      provider,
      online_payments_enabled: false,
      key_id: "",
      key_secret: "",
    }));

    setConfigured(false);
    setMaskedKey("");
    setError("");
  };

  const currentProvider = availableProviders.find(
    (p) => p.key === form.provider
  );

  const saveSettings = async (e) => {
    e.preventDefault();

    if (!paymentsFeatureEnabled) {
      setError(
        "Payment services are disabled for this hospital by MediBooks."
      );
      return;
    }

    if (!form.provider) {
      setError(
        "Please select a payment provider."
      );
      return;
    }

    /*
     * Current live provider implementation:
     * Razorpay
     */
    if (
      form.provider === "razorpay" &&
      form.online_payments_enabled &&
      !configured &&
      (
        !form.key_id.trim() ||
        !form.key_secret.trim()
      )
    ) {
      setError(
        "Razorpay Key ID and Key Secret are required before enabling online payments."
      );
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        online_payments_enabled:
          form.online_payments_enabled,

        provider:
          form.provider,

        mode:
          form.mode,
      };

      if (
        form.provider === "razorpay" &&
        form.key_id.trim()
      ) {
        payload.key_id =
          form.key_id.trim();
      }

      if (
        form.provider === "razorpay" &&
        form.key_secret.trim()
      ) {
        payload.key_secret =
          form.key_secret.trim();
      }

      const { data } = await api.patch(
        "/hospital/payment-settings",
        payload
      );

      setAvailableProviders(
        data.available_providers || []
      );

      setPaymentsFeatureEnabled(
        !!data.payments_feature_enabled
      );

      setConfigured(
        !!data.integration?.is_configured
      );

      setMaskedKey(
        data.integration?.key_id_masked || ""
      );

      setForm({
        online_payments_enabled:
          !!data.online_payments_enabled,

        provider:
          data.active_provider ||
          data.integration?.provider ||
          form.provider,

        mode:
          data.integration?.mode ||
          form.mode,

        key_id: "",
        key_secret: "",
      });

      toast.success(
        "Payment settings saved successfully"
      );
    } catch (e) {
      setError(
        apiError(
          e,
          "Failed to save payment settings"
        )
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-mb-fg2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading payment settings…
      </div>
    );
  }

  return (
    <div
      className="space-y-6 max-w-4xl"
      data-testid="hospital-payment-settings"
    >
      {/* Page heading */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400 inline-flex items-center gap-2">
          <CreditCard className="h-3 w-3" />
          Payments
        </div>

        <h1 className="mt-2 text-3xl font-light tracking-tight">
          Payment Integration
        </h1>

        <p className="mt-2 text-sm text-mb-fg2 max-w-2xl">
          Configure your hospital&apos;s payment gateway.
          Patient payments will be processed using this
          hospital&apos;s own payment account.
        </p>
      </div>

      {/* Super Admin disabled feature */}
      {!paymentsFeatureEnabled ? (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-6">
          <div className="flex items-start gap-4">
            <LockKeyhole className="h-5 w-5 text-amber-300 mt-0.5" />

            <div>
              <div className="text-base text-white font-medium">
                Online payments are not enabled
              </div>

              <div className="mt-2 text-sm text-mb-fg2 leading-6">
                Payment services for this hospital are currently
                disabled by MediBooks. Contact the MediBooks
                administrator to enable this feature.
              </div>
            </div>
          </div>
        </div>
      ) : availableProviders.length === 0 ? (
        /* No globally enabled provider */
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-6">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-5 w-5 text-amber-300 mt-0.5" />

            <div>
              <div className="text-base text-white font-medium">
                No payment provider available
              </div>

              <div className="mt-2 text-sm text-mb-fg2 leading-6">
                MediBooks has not enabled any payment gateway
                for hospitals yet. Contact the Super Admin.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <form
          onSubmit={saveSettings}
          className="space-y-6"
        >
          {/* Online payments toggle */}
          <div className="rounded-xl border border-mb-border bg-mb-surface p-6">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-teal-400">
                  Online payments
                </div>

                <div className="mt-2 text-lg font-light text-white">
                  Accept patient payments online
                </div>

                <div className="mt-1 text-xs text-mb-fg2 leading-5">
                  Turn this off if your hospital prefers cash,
                  reception payment or another offline method.
                </div>
              </div>

              <Switch
                checked={
                  form.online_payments_enabled
                }
                onCheckedChange={(value) =>
                  update(
                    "online_payments_enabled",
                    value
                  )
                }
                data-testid="hospital-online-payments-switch"
              />
            </div>
          </div>

          {/* Gateway configuration */}
          <div className="rounded-xl border border-mb-border bg-mb-surface p-6 space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-teal-400">
                Payment gateway
              </div>

              <div className="mt-2 text-lg font-light">
                {currentProvider?.name || "Payment"} configuration
              </div>

              <div className="mt-1 text-xs text-mb-fg2">
                {currentProvider?.description ||
                  "Configure your hospital payment provider."}
              </div>
            </div>

            {/* Provider + Mode */}
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">
                  Provider
                </Label>

                <select
                  value={form.provider}
                  onChange={(e) =>
                    changeProvider(
                      e.target.value
                    )
                  }
                  className="w-full h-10 rounded-md border border-mb-border bg-mb-surface2 px-3 text-sm text-white outline-none"
                  data-testid="payment-provider"
                >
                  {availableProviders.map(
                    (provider) => (
                      <option
                        key={provider.key}
                        value={provider.key}
                      >
                        {provider.name}
                      </option>
                    )
                  )}
                </select>

                <div className="text-[11px] text-mb-fg2">
                  Only providers enabled by MediBooks are shown.
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">
                  Mode
                </Label>

                <select
                  value={form.mode}
                  onChange={(e) =>
                    update(
                      "mode",
                      e.target.value
                    )
                  }
                  className="w-full h-10 rounded-md border border-mb-border bg-mb-surface2 px-3 text-sm text-white outline-none"
                  data-testid="payment-mode"
                >
                  <option value="test">
                    Test
                  </option>

                  <option value="live">
                    Live
                  </option>
                </select>
              </div>
            </div>

            {/* Configured status */}
            {configured && (
              <div className="rounded-lg border border-teal-500/20 bg-teal-500/5 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-teal-400 mt-0.5" />

                  <div>
                    <div className="text-sm text-white">
                      {currentProvider?.name || "Payment"} gateway configured
                    </div>

                    {maskedKey && (
                      <div className="mt-1 text-xs text-mb-fg2 font-mono">
                        Key ID: {maskedKey}
                      </div>
                    )}

                    <div className="mt-1 text-xs text-mb-fg2">
                      Leave credential fields empty to keep
                      the existing credentials.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Razorpay credentials */}
            {form.provider === "razorpay" && (
              <>
                <div className="space-y-2">
                  <Label
                    htmlFor="razorpay-key-id"
                    className="text-xs uppercase tracking-[0.2em] text-mb-fg2"
                  >
                    Razorpay Key ID
                  </Label>

                  <Input
                    id="razorpay-key-id"
                    type="text"
                    value={form.key_id}
                    onChange={(e) =>
                      update(
                        "key_id",
                        e.target.value
                      )
                    }
                    placeholder={
                      configured
                        ? "Leave empty to keep existing Key ID"
                        : "rzp_test_xxxxxxxxx"
                    }
                    autoComplete="off"
                    className="bg-mb-surface2 border-mb-border text-white"
                    data-testid="razorpay-key-id"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="razorpay-key-secret"
                    className="text-xs uppercase tracking-[0.2em] text-mb-fg2"
                  >
                    Razorpay Key Secret
                  </Label>

                  <Input
                    id="razorpay-key-secret"
                    type="password"
                    value={form.key_secret}
                    onChange={(e) =>
                      update(
                        "key_secret",
                        e.target.value
                      )
                    }
                    placeholder={
                      configured
                        ? "Leave empty to keep existing secret"
                        : "Enter Razorpay Key Secret"
                    }
                    autoComplete="new-password"
                    className="bg-mb-surface2 border-mb-border text-white"
                    data-testid="razorpay-key-secret"
                  />

                  <div className="text-[11px] text-mb-fg2">
                    The secret is never displayed again after saving.
                  </div>
                </div>
              </>
            )}

            {/* Future providers */}
            {form.provider !== "razorpay" && (
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-mb-fg2">
                Credential configuration for{" "}
                <span className="text-white">
                  {currentProvider?.name}
                </span>{" "}
                will appear here when that provider adapter is enabled.
              </div>
            )}

            {/* Error */}
            {error && (
              <div
                className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
                data-testid="payment-settings-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={saving || !form.provider}
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950"
              data-testid="payment-settings-save"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save payment settings"
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}